import React, { useEffect, useState } from "react";
import { Typography, List, ListItem, ListItemText, Divider, Button, Box, CircularProgress } from "@mui/material";
import { CartType } from "../../models/CartType";
import { useSignedInUserState } from "../../hooks/useAuthState";
import { useNavigate } from "react-router-dom";
import { useCart } from "../../hooks/contexts/cartContext";
import { OrderHistoryService } from "../../services/order/orderHistoryService";
import { FullProperty } from "../../models/fullProperty";
import { StringsService } from "../../services/strings/strings";
import { MetricsNames } from "../../services/firebase/metricsService";
import LocationOnIcon from '@mui/icons-material/LocationOn';
import Lottie from "lottie-react";
import { logEvent } from "../../firebase";

const OrderSuccessPage = () => {
    const [cartItemLabels, setCartItemLabels] = useState<{ [cartItemId: string]: string }>({})
    const [cart, setCart] = useState<CartType | null>(null);
    const [restaurant, setRestaurant] = useState<FullProperty | null>(null);
    const { profile } = useSignedInUserState();
    const navigate = useNavigate()
    const { fetchCart } = useCart();

    const animationPath = "/animation/common_loading_animation.json";
    const [animationData, setAnimationData] = useState<any>(null);

    const animationSuccessPath = "/animation/order_success_animation.json";
    const [animationSuccessData, setAnimationSuccessData] = useState<any>(null);

    useEffect(() => {
        fetch(animationSuccessPath)
            .then((response) => response.json())
            .then((data) => setAnimationSuccessData(data))
            .catch((error) => console.error("Error loading Lottie animation:", error));
    }, [animationPath]);

    useEffect(() => {
        fetch(animationPath)
            .then((response) => response.json())
            .then((data) => setAnimationData(data))
            .catch((error) => console.error("Error loading Lottie animation:", error));
    }, [animationPath]);

    useEffect(() => {
        const load = async () => {
            fetchCart();
            if (profile) {
                const data = await OrderHistoryService.getLatestOrder(profile);
                if (!data) {
                    setCart(null);
                    setRestaurant(null);
                } else {
                    setCart(data.cart);
                    setRestaurant(data.restaurant);
                    logEvent(MetricsNames.PURCHASE_SUCCESS);
                    logEvent(MetricsNames.PURCHASE_DETAILS, {
                        "value": data.cart.totalAmount,
                        "restaurantId": data.cart.restaurantId
                    })

                    let newCartItemLabels: { [cartItemId: string]: string } = {}
                    const promises = data.cart.items.map(async (element) => {
                        newCartItemLabels[element.menuCartItem.id] = `${element.menuCartItem.name} ${element.quantity}x`
                    });
                    await Promise.all(promises)
                    setCartItemLabels(newCartItemLabels)
                }
            }
        }
        load();
    }, [profile]);

    if (!cart) {
        return (
            <Box
                sx={{
                    minHeight: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    padding: 8,
                }}
                className="patlite"
            >
                <Box
                    sx={{
                        backgroundColor: "#fff",
                        boxShadow: 3,
                        borderRadius: 2,
                        padding: 8,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        maxWidth: 400,
                        transform: "translateY(100px) scale(0.95)",
                        animation: "slideUpBounce 1.5s cubic-bezier(0.2, 1, 0.3, 1) forwards",
                        "@keyframes slideUpBounce": {
                            "0%": {
                                opacity: 0,
                                transform: "translateY(100px) scale(0.95)",
                            },
                            "50%": {
                                opacity: 1,
                                transform: "translateY(-20px) scale(1.1)",
                            },
                            "100%": {
                                opacity: 1,
                                transform: "translateY(0) scale(1)",
                            },
                        },
                    }}
                >
                    <Lottie animationData={animationData} style={{ width: 200, height: 200 }} />

                    <Typography
                        variant="h6"
                        textAlign={"center"}
                        sx={{
                            fontWeight: "bold",
                            marginTop: 4,
                            color: "#162C1F",
                            maxWidth: 600
                        }}
                    >
                        Preparing your conscious order...
                    </Typography>

                    <Typography
                        variant="body2"
                        textAlign={"center"}
                        sx={{
                            marginTop: 2,
                            maxWidth: 500,
                            color: "text.secondary"
                        }}
                    >
                        Good food for a better planet. 🌱
                    </Typography>
                </Box>
            </Box>
        );
    }


    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "100vh",
                textAlign: "center",
                padding: 3,
            }}
            className="patlite"
        >
            <Box
                sx={{
                    backgroundColor: "#fff",
                    padding: 4,
                    borderRadius: 4,
                    boxShadow: 3,
                    maxWidth: 400,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    opacity: 0,
                    transform: "translateY(100px) scale(0.95)",
                    animation: "slideUpBounce 1.5s cubic-bezier(0.2, 1, 0.3, 1) forwards",
                    "@keyframes slideUpBounce": {
                        "0%": {
                            opacity: 0,
                            transform: "translateY(100px) scale(0.95)",
                        },
                        "50%": {
                            opacity: 1,
                            transform: "translateY(-20px) scale(1.1)",
                        },
                        "100%": {
                            opacity: 1,
                            transform: "translateY(0) scale(1)",
                        },
                    },
                }}
            >
                <Lottie animationData={animationSuccessData} style={{ width: 200, height: 200 }} />
                <Typography variant="h4" gutterBottom>
                    Order Successful!
                </Typography>

                <Typography variant="body1" sx={{ marginBottom: 1 }}>
                    Your order is ready for collection!
                </Typography>


                {
                    cart && !cart.usesCustomerSettlement &&
                    <Typography variant="body2" sx={{ marginBottom: 1, color: "text.secondary" }}>
                        <strong>
                            Order Collection #: {cart?.orderCollectionNumber}<br />
                        </strong>
                    </Typography>
                }

                <Typography variant="body2" sx={{ marginBottom: 1, color: "text.secondary" }}>
                    {
                        (cart && cart.usesCustomerSettlement) ?
                            "Please click `Collect Order` on your order history page when you arrive at the store, where you'll also find directions and your order number."
                            :
                            "When you arrive at the store, please click `Collect Order` in your order history page and show your confirmation."
                    }
                </Typography>

                <Button
                    variant="contained"
                    sx={{ margin: "8px 0", background: "#162C1F", border: "1px solid #162C1F", borderRadius: "24px", width: "100%" }}
                    onClick={() => {
                        const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${restaurant?.location.lat},${restaurant?.location.lng}`;
                        window.open(mapsUrl, "_blank");
                    }}
                    startIcon={<LocationOnIcon />}
                >
                    Get Directions
                </Button>

                <Button
                    variant="outlined"
                    sx={{ margin: "0 8px", border: "1px solid #162C1F", borderRadius: "24px", width: "100%" }}
                    onClick={() => navigate("/order/history")}
                >
                    <Typography variant="button" component="h1" sx={{ color: "#162C1F" }}>
                        Order History
                    </Typography>

                </Button>

                <Typography variant="body1" sx={{ margin: "8px 0" }}>
                    Thanks for choosing Eco Eats!
                </Typography>
            </Box>
        </Box>

    );

};


export default OrderSuccessPage;
